import InteractiveAnnotation from "./interactiveAnnotation";
import { POINTER_CLASSES } from "../../config/config";
import { isInBoundsBox, sidesFlag, sidesAndCorners, sideCornerDetails, getArcPosition } from "../geometry";
import { boxSize, setLineStyle, drawEllipse } from "../canvasUtils";


export default class Ellipse extends InteractiveAnnotation {
  constructor(position, options, radius = 100) {
    super(position, options);

    this.centerX = position.x;
    this.centerY = position.y;
    this.radiusX = radius;
    this.radiusY = radius;

    this.activeBody = true;
    // active sides ORs the sidesFlag flag to represent selection of a side or corner
    this.activeSides = 0;
  }

  get isActive() {
    return this.activeBody || this.activeSides;
  }

  get area() {
    return Math.PI * this.radiusX * this.radiusY;
  }

  get rx2() { return this.radiusX * this.radiusX; }
  get ry2() { return this.radiusY * this.radiusY; }

  draw(ctx) {
    setLineStyle(ctx, this);

    ctx.beginPath();
    drawEllipse(ctx, this.centerX, this.centerY, this.radiusX, this.radiusY);
    ctx.closePath();
    ctx.stroke();
  }

  isInBounds(x, y) {
    return this.isOnEllipse(x, y) || this.getHoveredBox(x, y);
  }

  isWithinEllipse(x, y){
    let dx = x - this.centerX;
    let dy = y - this.centerY;
    let scaledDx = dx * dx / this.rx2;
    let scaledDy = dy * dy / this.ry2;
    return scaledDx + scaledDy < 1;
  }

  isOnEllipse(x, y) {
    let dx = x - this.centerX;
    let dy = y - this.centerY;

    let angle = -Math.atan2(dy, dx) + Math.PI / 2;
    let pos = this.getAngularPosition(angle);

    let diffX = Math.abs(x - pos.x);
    let diffY = Math.abs(y - pos.y);

    return diffX <= boxSize && diffY <= boxSize;
  }

  // returns the position on the edge of the ellipse that is passed through by a
  // line with the given angle that passes through the origin
  getAngularPosition(theta){
    let tan = Math.tan(theta);
    let tan2 = tan * tan;
    let denominator = Math.sqrt(this.rx2 + this.ry2 * tan2);

    let adjustmentX = this.radiusX * this.radiusY * tan / denominator;
    let adjustmentY = this.radiusX * this.radiusY / denominator;

    let signAdjust = Math.abs(theta) < (Math.PI / 2) ? 1 : -1;
    return {x: this.centerX + adjustmentX * signAdjust, y: this.centerY + adjustmentY * signAdjust};
  }


  getInteractionPoints() {
    return sidesAndCorners.map(flag => this.getSideCornerPosition(flag));
  }

  getHoveredBox(x, y) {
    for (let i = 0; i < sidesAndCorners.length; i++) {
      const flag = sidesAndCorners[i];
      const details = sideCornerDetails[flag];
      const pos = this.getArcPosition(details.theta);
      if (isInBoundsBox(x, y, pos.x, pos.y, this.transform.zoom)) return details;
    }
    return null;
  }

  getSideCornerPosition(flag) {
    const details = sideCornerDetails[flag];
    return this.getArcPosition(details.theta);
  }

  // returns the position on the edge of the ellipse that is arced through by the given angle
  // note - this does not equal the angular position!
  getArcPosition(theta) {
    return getArcPosition(this.centerX, this.centerY, this.radiusX, this.radiusY, theta);
  }

  getHoverClass(x, y) {
    let box = this.getHoveredBox(x, y);
    if (box) return box.hoverClass;
    return POINTER_CLASSES.pointer;
  }

  mousedown(x, y) {
    super.mousedown(x, y);
    let box = this.getHoveredBox(x, y);
    this.activeSides = box && box.side;
    this.activeBody = !box;
  }

  mousemove(x, y) {
    if (this.activeBody) this.updateBody(x, y);
    else if (this.activeSides) this.updateSides(x, y);
  }

  updateBody(x, y) {
    const { dx, dy } = this.getAndUpdateInteraction(x, y);
    this.centerX += dx;
    this.centerY += dy;
  }

  updateSides(x, y) {
    const { dx, dy } = this.getAndUpdateInteraction(x, y);

    let top = this.activeSides & sidesFlag.top;
    let bottom = this.activeSides & sidesFlag.bottom;
    let left = this.activeSides & sidesFlag.left;
    let right = this.activeSides & sidesFlag.right;
    let corner = (top | bottom) && (left | right);
    const cornerAdjust = corner ? 1.17 : 1; // not sure how this constant is derived

    let moveY = top || bottom ? 1 : 0;
    let increaseY = top ? -1 : 1;
    this.centerY += cornerAdjust * moveY * dy / 2;
    this.radiusY += cornerAdjust * moveY * increaseY * dy / 2;
    if (this.radiusY <= 0) this.flipY();
    
    let moveX = left || right ? 1 : 0;
    let increaseX = left ? -1 : 1;
    this.centerX += cornerAdjust * moveX * dx / 2;
    this.radiusX += cornerAdjust * moveX * increaseX * dx / 2;
    if (this.radiusX <= 0) this.flipX();
  }

  flipY() {
    this.radiusY *= -1;

    if (this.activeSides & sidesFlag.top) {
      // if updating top, set updatingBottom to true
      this.activeSides &= ~sidesFlag.top;
      this.activeSides |= sidesFlag.bottom;
    } else {
      this.activeSides &= ~sidesFlag.bottom;
      this.activeSides |= sidesFlag.top;
    }
  }

  flipX() {
    this.radiusX *= -1;

    if (this.activeSides & sidesFlag.right) {
      // set updatingleft to true
      this.activeSides &= ~sidesFlag.right;
      this.activeSides |= sidesFlag.left;
    } else {
      // set updatingRight to true
      this.activeSides &= ~sidesFlag.left;
      this.activeSides |= sidesFlag.right;
    }
  }

  deactivate(){
    this.activeBody = false;
    this.activeSides = 0;
  }
}